/**
 * Get the correct price based on table type
 * @param {Object} item - The menu item object
 * @param {string} tableType - The table type (e.g., "AC", "Garden")
 * @returns {number} The price for the specified table type
 */
export const getPriceByTableType = (item, tableType) => {
    // Calculate topping price
    let toppingPrice = 0;
    if (item?.activeTopping && Array.isArray(item.activeTopping)) {
      item.activeTopping.forEach((topping) => {
        toppingPrice += topping.itemPrice || 0;
      });
    }
    
    // Map table type names to price fields
    const priceFieldMap = {
      "AC": "priceForAC",
      "Non AC": "priceForNonAC",
      "Garden": "priceForGarden",
      "Banquet Hall": "priceForBanquetHall",
      "Hut": "priceForHut",
      "Online": "priceForOnline",
      "Room Service": "priceForRoomService",
      "HD": "priceForHD",
      "Parcel": "priceForParcel",
    };
  
    const priceField = priceFieldMap[tableType];
    const basePrice = priceField ? (item[priceField] || item.itemPrice) : item.itemPrice;
    
    return basePrice + toppingPrice;
};
  
  /**
   * Get table type from tables array based on order
   * Handles special cases like Parcel (P1, P2) and Home Delivery (H1, H2)
   * @param {Array} tables - Array of all tables
   * @param {Object} currentOrder - Current order object
   * @returns {string} Table type string
   */
  export const getTableTypeFromOrder = (tables, currentOrder) => {
    if (!currentOrder) return "AC";

    const tableNumber = currentOrder?.tableNumber;
    const orderType = currentOrder?.orderType;
    
    // Check by order type first
    if (orderType === "Home Delivery") {
      return "HD";
    }
    
    if (orderType === "Parcel") {
      return "Parcel";
    }
    
    // Find table by tableTypeId for regular dine-in tables
    const tableTypeId = currentOrder?.tableTypeId;
    if (tableTypeId) {
      const currentTable = tables?.find(table => table.tableTypeId === tableTypeId);
      if (currentTable) {
        return currentTable.tableType;
      }
    }
    // Default fallback
    // return "AC";
  };
  
  /**
   * Parse float to 2 decimal places
   * @param {number} value - Value to parse
   * @returns {number} Parsed value
   */
  export const parseFloat2Decimals = (value) => {
    return parseFloat(parseFloat(value).toFixed(2));
  };
  
  /**
   * Get meal name by IST time
   * @param {Array} itemAvailability - Item availability array
   * @param {Date} currentIST - Current IST date
   * @returns {string} Meal name
   */
  export const getMealNameByIST = (itemAvailability, currentIST = new Date()) => {
    const offsetMs = 5.5 * 60 * 60 * 1000;
  
    const foundMeal = itemAvailability.find(meal => {
      const startIST = new Date(new Date(meal.mealStartTime).getTime() + offsetMs);
      const endIST = new Date(new Date(meal.mealEndTime).getTime() + offsetMs);
      return currentIST >= startIST && currentIST <= endIST;
    });
  
    if (foundMeal) return foundMeal.mealName;
  
    const hours = currentIST.getHours();
    return hours >= 3 && hours < 15 ? "Lunch" : "Dinner";
  };
  
  /**
   * Get payment type string from payment methods array
   * @param {Array} paymentMethods - Array of payment method objects
   * @returns {string} Comma-separated payment types
   */
  export const getPaymentType = (paymentMethods) => {
    return paymentMethods
      .map((t) => t.paymentMethodType)
      .join(", ");
  };
  
  /**
   * Get grouped order items by KOT
   * @param {Object} currentOrder - Current order object
   * @returns {Array} Array of grouped KOT items
   */
  export const getGroupedOrderItems = (currentOrder) => {
    if (!currentOrder || !currentOrder.KOTS) return [];
    
    return currentOrder.KOTS.map((kot, index) => ({
      kotNumber: kot.kotNumber,
      kotIndex: index,
      items: kot.orderItems || [],
    }));
  };
  
  /**
   * Calculate discount percentage
   * @param {number} grandTotal - Grand total amount
   * @param {number} percent - Discount percentage
   * @returns {number} Discount amount
   */
  export const calculateDiscountPercentage = (grandTotal, percent) => {
    const result = (percent * grandTotal) / 100;
    return parseFloat(result.toFixed(2));
  };
  
  /**
   * Get round-off type based on settings
   * @param {boolean} lowerRoundOff - Lower round off enabled
   * @param {boolean} upperRoundOff - Upper round off enabled
   * @returns {string} Round-off type string
   */
  export const getRoundOffType = (lowerRoundOff, upperRoundOff) => {
    if (lowerRoundOff) return "lowerRoundOff";
    if (upperRoundOff) return "upperRoundOff";
    return "standardRoundOff";
  };
  
  /**
   * Get complete order totals with rounding
   * @param {Object} order - Order object
   * @param {number} cgst - CGST rate
   * @param {number} sgst - SGST rate
   * @param {string} tableType - Table type
   * @param {boolean} exclusiveGST - Exclusive GST flag
   * @param {string} roundOffType - Round-off type
   * @param {Function} calculateOrderTotals - Calculate totals function
   * @param {Function} roundOffTotal - Round off total function
   * @returns {Object} Order totals with rounded grand total
   */
  export const getCompleteOrderTotals = (
    order,
    cgst,
    sgst,
    tableType,
    exclusiveGST,
    roundOffType,
    calculateOrderTotals,
    roundOffTotal
  ) => {
    if (!order) {
      return {
        grandTotal: 0,
        roundedGrandTotal: 0,
        roundOffValue: 0,
      };
    }
  
    const discount = order?.discount || 0;
    const otherCharges = order?.otherCharges || 0;
  
    const totals = calculateOrderTotals(
      order,
      cgst,
      sgst,
      otherCharges,
      discount,
      tableType,
      exclusiveGST
    );
  
    const roundedGrandTotal = roundOffTotal(totals.grandTotal || 0, roundOffType);
    const roundOffValue = roundedGrandTotal - (totals.grandTotal || 0);
  
    return {
      ...totals,
      roundedGrandTotal,
      roundOffValue,
    };
  };

  /**
   * Map order items with the correct price based on table type
   * @param {Array} orderItems - Array of order items
   * @param {string} tableType - Table type string
   * @returns {Array} Mapped order items with table-specific itemPrice and itemTotal
   */
  export const mapOrderItemsWithTablePrice = (orderItems, tableType) => {
    if (!orderItems) return [];
    return orderItems.map((item) => {
      const itemPrice = getPriceByTableType(item, tableType) || item.itemPrice;
      return {
        ...item,
        itemPrice,
        itemTotal: Number((item.quantity * itemPrice).toFixed(2)),
      };
    });
  };